import { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Save, Loader2, CheckCircle2, Megaphone, Newspaper, Users, Upload, X } from "lucide-react";
import { Link } from "react-router-dom";

const DEFAULT_POPUP = { enabled: false, scroll_percent: 50, image_url: "", link_url: "", html: "", title: "" };
const DEFAULT_GF = { enabled: true, publication_url: "" };

export default function SettingsPage() {
  const [popup, setPopup] = useState(DEFAULT_POPUP);
  const [gf, setGf] = useState(DEFAULT_GF);
  const [subs, setSubs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState("");
  const [err, setErr] = useState("");
  const [uploadingAd, setUploadingAd] = useState(false);
  const adFileRef = useRef(null);

  const onAdImagePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\//.test(file.type)) { setErr("केवल image चुनें"); return; }
    if (file.size > 5 * 1024 * 1024) { setErr("Image 5MB से छोटी हो"); return; }
    setUploadingAd(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPopup((p) => ({ ...p, image_url: data.url }));
    } catch (e2) {
      setErr(apiErr(e2.response?.data?.detail) || "Image अपलोड विफल");
    } finally {
      setUploadingAd(false);
      if (adFileRef.current) adFileRef.current.value = "";
    }
  };

  const load = async () => {
    try {
      const [p, g, s] = await Promise.all([
        api.get("/settings/popup-ad"),
        api.get("/settings/google-follow"),
        api.get("/subscribers").catch(() => ({ data: [] })),
      ]);
      setPopup({ ...DEFAULT_POPUP, ...p.data });
      setGf({ ...DEFAULT_GF, ...g.data });
      setSubs(s.data || []);
    } catch (e) { setErr(apiErr(e.response?.data?.detail) || "Load failed"); }
  };

  useEffect(() => { load(); }, []);

  const savePopup = async () => {
    setBusy(true); setErr(""); setOk("");
    try {
      await api.put("/settings/popup-ad", popup);
      setOk("Popup Ad सहेजा गया");
      setTimeout(() => setOk(""), 2500);
    } catch (e) { setErr(apiErr(e.response?.data?.detail) || "सेव नहीं हुआ"); }
    finally { setBusy(false); }
  };

  const saveGF = async () => {
    setBusy(true); setErr(""); setOk("");
    try {
      await api.put("/settings/google-follow", gf);
      setOk("Google Follow सहेजा गया");
      setTimeout(() => setOk(""), 2500);
    } catch (e) { setErr(apiErr(e.response?.data?.detail) || "सेव नहीं हुआ"); }
    finally { setBusy(false); }
  };

  const removeSub = async (id) => {
    if (!window.confirm("इस सब्सक्राइबर को हटाएँ?")) return;
    await api.delete(`/subscribers/${id}`);
    setSubs((s) => s.filter((x) => x.id !== id));
  };

  return (
    <div className="max-w-5xl space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 font-serif-dev">साइट सेटिंग्स</h1>
        <p className="text-sm text-gray-500 mt-1">Popup Ad, Google Follow, न्यूज़लेटर सब्सक्राइबर्स</p>
      </div>

      {err && <div className="text-sm text-[#D92525] bg-red-50 rounded px-4 py-2.5">{err}</div>}
      {ok && <div className="text-sm text-green-700 bg-green-50 rounded px-4 py-2.5 flex items-center gap-2"><CheckCircle2 size={15} /> {ok}</div>}

      {/* Popup Ad */}
      <section className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm space-y-4" data-testid="settings-popup-section">
        <div className="flex items-center gap-2">
          <Megaphone className="text-[#D92525]" size={18} />
          <h2 className="text-lg font-bold text-gray-900">Popup Ad</h2>
        </div>
        <p className="text-sm text-gray-500 -mt-2">जब visitor पेज का दिया गया % scroll कर लेता है, तब dismissible ad modal दिखता है (per session बस एक बार)।</p>

        <label className="flex items-center gap-2 cursor-pointer">
          <input data-testid="popup-enabled" type="checkbox" checked={popup.enabled} onChange={(e) => setPopup({ ...popup, enabled: e.target.checked })} className="accent-[#D92525]" />
          <span className="text-sm font-medium text-gray-700">Popup Ad चालू करें</span>
        </label>

        <label className="block">
          <span className="text-[11px] uppercase tracking-[0.18em] text-gray-500">Scroll Trigger (%)</span>
          <input data-testid="popup-scroll" type="number" min="10" max="95" value={popup.scroll_percent} onChange={(e) => setPopup({ ...popup, scroll_percent: parseInt(e.target.value) || 50 })}
            className="mt-1 w-32 border border-gray-200 rounded-lg py-2 px-3 focus:border-[#D92525] outline-none" />
        </label>

        <label className="block">
          <span className="text-[11px] uppercase tracking-[0.18em] text-gray-500">शीर्षक (optional)</span>
          <input data-testid="popup-title" value={popup.title} onChange={(e) => setPopup({ ...popup, title: e.target.value })}
            className="mt-1 w-full border border-gray-200 rounded-lg py-2 px-3 focus:border-[#D92525] outline-none" placeholder="विज्ञापन · Advertisement" />
        </label>

        <div className="block">
          <span className="text-[11px] uppercase tracking-[0.18em] text-gray-500">विज्ञापन फोटो (सीधे upload)</span>
          <div className="mt-2 flex items-start gap-4">
            {popup.image_url ? (
              <div className="relative shrink-0">
                <img src={popup.image_url} alt="ad preview" data-testid="popup-image-preview"
                  className="w-32 h-32 object-cover rounded-lg border border-gray-200 bg-gray-50" />
                <button type="button" onClick={() => setPopup({ ...popup, image_url: "" })}
                  data-testid="popup-image-remove"
                  className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-[#D92525] text-white flex items-center justify-center shadow"
                  title="हटाएँ"><X size={12} /></button>
              </div>
            ) : (
              <div className="w-32 h-32 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 flex flex-col items-center justify-center text-gray-400 shrink-0">
                <Upload size={20} className="mb-1" />
                <span className="text-[10px]">फोटो जोड़ें</span>
              </div>
            )}
            <div className="flex-1 space-y-2">
              <input
                ref={adFileRef}
                type="file"
                accept="image/*"
                onChange={onAdImagePick}
                disabled={uploadingAd}
                data-testid="popup-image-file"
                className="block w-full text-xs text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#0F0F0F] file:text-white hover:file:bg-[#D92525] disabled:opacity-60"
              />
              {uploadingAd && <div className="text-xs text-gray-500 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> अपलोड हो रहा है…</div>}
              <p className="text-[10px] text-gray-400">JPG / PNG · max 5MB · अपने आप server पर upload हो जाएगी</p>
              <details className="text-[11px] text-gray-500">
                <summary className="cursor-pointer hover:text-gray-700">या Image URL डालें (advanced)</summary>
                <input value={popup.image_url} onChange={(e) => setPopup({ ...popup, image_url: e.target.value })}
                  data-testid="popup-image"
                  className="mt-2 w-full border border-gray-200 rounded py-1.5 px-2 text-xs focus:border-[#D92525] outline-none"
                  placeholder="https://..." />
              </details>
            </div>
          </div>
        </div>
        <label className="block">
          <span className="text-[11px] uppercase tracking-[0.18em] text-gray-500">Link URL (photo पर click होने पर खुले)</span>
          <input data-testid="popup-link" value={popup.link_url} onChange={(e) => setPopup({ ...popup, link_url: e.target.value })}
            className="mt-1 w-full border border-gray-200 rounded-lg py-2 px-3 focus:border-[#D92525] outline-none" placeholder="https://..." />
        </label>

        <div className="text-[11px] uppercase tracking-[0.18em] text-gray-400">— या —</div>

        <label className="block">
          <span className="text-[11px] uppercase tracking-[0.18em] text-gray-500">Custom HTML (जैसे AdSense स्क्रिप्ट snippet)</span>
          <textarea data-testid="popup-html" rows={5} value={popup.html} onChange={(e) => setPopup({ ...popup, html: e.target.value })}
            className="mt-1 w-full border border-gray-200 rounded-lg py-2 px-3 focus:border-[#D92525] outline-none font-mono text-xs" placeholder="<ins class='adsbygoogle' ... />" />
        </label>

        <button data-testid="popup-save" onClick={savePopup} disabled={busy}
          className="inline-flex items-center gap-2 bg-[#D92525] text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#0A0F1E] disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} सहेजें
        </button>
      </section>

      {/* Google Follow */}
      <section className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm space-y-4" data-testid="settings-gf-section">
        <div className="flex items-center gap-2">
          <Newspaper className="text-[#D92525]" size={18} />
          <h2 className="text-lg font-bold text-gray-900">Google News Follow Button</h2>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input data-testid="gf-enabled" type="checkbox" checked={gf.enabled} onChange={(e) => setGf({ ...gf, enabled: e.target.checked })} className="accent-[#D92525]" />
          <span className="text-sm font-medium text-gray-700">Footer में Google Follow बटन दिखाएँ</span>
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-[0.18em] text-gray-500">Publication URL (Google Publisher Center से)</span>
          <input data-testid="gf-url" value={gf.publication_url} onChange={(e) => setGf({ ...gf, publication_url: e.target.value })}
            className="mt-1 w-full border border-gray-200 rounded-lg py-2 px-3 focus:border-[#D92525] outline-none" placeholder="https://news.google.com/publications/..." />
          <p className="text-xs text-gray-400 mt-1">खाली छोड़ें तो Google News search page पर जाएगा।</p>
        </label>
        <button data-testid="gf-save" onClick={saveGF} disabled={busy}
          className="inline-flex items-center gap-2 bg-[#D92525] text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#0A0F1E] disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} सहेजें
        </button>
      </section>

      {/* Subscribers */}
      <section className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm" data-testid="settings-subs-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users className="text-[#D92525]" size={18} />
            <h2 className="text-lg font-bold text-gray-900">Newsletter सब्सक्राइबर्स ({subs.length})</h2>
          </div>
        </div>
        {subs.length === 0 ? (
          <div className="text-sm text-gray-400 py-6 text-center">अभी कोई सब्सक्राइबर नहीं</div>
        ) : (
          <div className="max-h-[420px] overflow-y-auto border rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider sticky top-0">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">ईमेल</th>
                  <th className="text-left px-4 py-2 font-medium">दिनांक</th>
                  <th className="text-right px-4 py-2 font-medium">कार्रवाई</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {subs.map((s) => (
                  <tr key={s.id} data-testid={`sub-${s.id}`}>
                    <td className="px-4 py-2">{s.email}</td>
                    <td className="px-4 py-2 text-gray-500 text-xs">{s.created_at?.slice(0, 10)}</td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => removeSub(s.id)} className="text-[#D92525] hover:underline text-xs">हटाएँ</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div className="text-xs text-gray-400">
        <Link to="/admin" className="link-underline">← वापस डैशबोर्ड</Link>
      </div>
    </div>
  );
}
